import React, { useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import {
  Input,
  Button,
  Space,
  Spin,
  Collapse,
  Tag,
  Alert,
  Modal,
  message,
  Popconfirm,
  Switch,
} from 'antd';
import { SaveOutlined, RollbackOutlined } from '@ant-design/icons';
import { getSubTypeLabel } from '../../Config/subType';
import { PageContainer, PageHeader, SectionCard } from '../../common';

/**
 * EditQSetPage — 設問セット編集画面
 * ------------------------------------------------------------------
 * 機能企画書 F-02 (セット名変更) + F-04 (質問·カテゴリ編集) に対応。
 *
 * URL: /editqset/:id
 *
 * 編集可能項目:
 *   - セット名 (set_name)
 *   - 各質問のテキスト (question)
 *   - 各質問のカテゴリ階層:
 *       category_name (小)、category_name_upper1 (大)、category_name_upper2 (中)
 *
 * カテゴリ階層 UI ルール (사용자 정책):
 *   - 大·中·小 それぞれ独立した Switch トグルで表示/非表示を制御
 *   - トグル ON → 全質問に그 階層の入力欄が現れる
 *   - トグル OFF (値が既にある場合は確認ダイアログ) → 全質問から그 階層を削除
 *   - 保存時に混合状態 (一部のみ空) は確認ダイアログを表示
 *     → 確認すると그 階層 자체를 자동 제거
 *     → 취소하면 저장 중단
 *
 * 空値判定: trim(半角·全角空白) 후에 空ならフィールド自体を保存しない
 */

// 半角·全角空白を両端から除去 (中の空白は保持)
const trimWide = (v) =>
  typeof v === 'string' ? v.replace(/^[\s　]+|[\s　]+$/g, '') : v;

const labelStyle = {
  fontSize: 'var(--text-sm)',
  color: 'var(--grey-65)',
  marginBottom: 'var(--space-1)',
  fontWeight: 'var(--font-medium)',
};

const EditQSetPage = () => {
  const history = useHistory();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [questionSet, setQuestionSet] = useState(null);
  const [originalSetName, setOriginalSetName] = useState('');
  const [setName, setSetName] = useState('');
  const [questions, setQuestions] = useState([]);
  // _id → {question, category_name, category_name_upper1, category_name_upper2}
  const [originalQuestions, setOriginalQuestions] = useState({});

  // カテゴリ階層 表示 ON/OFF (3レベル独立, セット全体)
  // upper1 = 大, upper2 = 中, category_name = 小
  const [showUpper1, setShowUpper1] = useState(false);
  const [showUpper2, setShowUpper2] = useState(false);
  const [showCategory, setShowCategory] = useState(true);
  // 初期値 (변경 감지용)
  const [initialShowUpper1, setInitialShowUpper1] = useState(false);
  const [initialShowUpper2, setInitialShowUpper2] = useState(false);
  const [initialShowCategory, setInitialShowCategory] = useState(true);

  // -------------------------------------------------------------------
  //  詳細 fetch + 階層 자동 감지
  // -------------------------------------------------------------------
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await Axios.get(`/api/questionSet/${id}/detail`);
        if (res.data.success) {
          const set = res.data.questionSet;
          const qs = res.data.questions || [];
          setQuestionSet(set);
          setOriginalSetName(set.set_name || '');
          setSetName(set.set_name || '');
          setQuestions(qs);

          // 元値 기록 (差分 검출용)
          const orig = {};
          qs.forEach((q) => {
            orig[String(q._id)] = {
              question: q.question || '',
              category_name: q.category_name || '',
              category_name_upper1: q.category_name_upper1 || '',
              category_name_upper2: q.category_name_upper2 || '',
            };
          });
          setOriginalQuestions(orig);

          // 階層 감지: 한 질문이라도 값이 있으면 그 階層 ON으로 시작
          const anyU1 = qs.some(
            (q) =>
              q.category_name_upper1 &&
              trimWide(q.category_name_upper1) !== ''
          );
          const anyU2 = qs.some(
            (q) =>
              q.category_name_upper2 &&
              trimWide(q.category_name_upper2) !== ''
          );
          const anyCat = qs.some(
            (q) => q.category_name && trimWide(q.category_name) !== ''
          );
          setShowUpper1(anyU1);
          setShowUpper2(anyU2);
          setShowCategory(anyCat);
          setInitialShowUpper1(anyU1);
          setInitialShowUpper2(anyU2);
          setInitialShowCategory(anyCat);
        } else {
          message.error('セット詳細の取得に失敗しました');
        }
      } catch (error) {
        console.log(error);
        message.error('読み込みエラー');
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  // -------------------------------------------------------------------
  //  カテゴリ別 그룹화
  // -------------------------------------------------------------------
  const grouped = useMemo(() => {
    const map = new Map();
    questions.forEach((q) => {
      const cat = q.category_name_upper1 || '(カテゴリ未設定)';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(q);
    });
    return Array.from(map.entries());
  }, [questions]);

  // -------------------------------------------------------------------
  //  変更検出
  // -------------------------------------------------------------------
  // 質問별 변경 여부 (question/category 합산)
  const isQuestionChanged = (q) => {
    const o = originalQuestions[String(q._id)] || {};
    if ((q.question || '') !== (o.question || '')) return true;
    // 階層 ON↔OFF 토글된 경우는 모두 변경된 것으로 간주
    if (showUpper1 !== initialShowUpper1) return true;
    if (showUpper2 !== initialShowUpper2) return true;
    if (showCategory !== initialShowCategory) return true;
    // 階層이 ON인 경우 값 비교
    const fields = [];
    if (showCategory) fields.push('category_name');
    if (showUpper1) fields.push('category_name_upper1');
    if (showUpper2) fields.push('category_name_upper2');
    return fields.some(
      (f) => trimWide(q[f] || '') !== trimWide(o[f] || '')
    );
  };

  const changedQuestions = questions.filter(isQuestionChanged);
  const isSetNameChanged = setName !== originalSetName;
  const hasAnyChanges = isSetNameChanged || changedQuestions.length > 0;

  // -------------------------------------------------------------------
  //  필드 갱신 핸들러
  // -------------------------------------------------------------------
  const updateField = (qId, field, value) => {
    setQuestions((prev) =>
      prev.map((q) =>
        String(q._id) === String(qId) ? { ...q, [field]: value } : q
      )
    );
  };

  // -------------------------------------------------------------------
  //  カテゴリ階層 トグル (3レベル独立)
  //  ON: そのまま有効化 → 입력란 표시
  //  OFF: 값이 이미 있으면 확인 다이얼로그
  // -------------------------------------------------------------------
  const LEVEL_META = {
    upper1: { field: 'category_name_upper1', label: '大カテゴリ' },
    upper2: { field: 'category_name_upper2', label: '中カテゴリ' },
    category: { field: 'category_name', label: '小カテゴリ' },
  };

  const setShowByLevel = (level, value) => {
    if (level === 'upper1') setShowUpper1(value);
    if (level === 'upper2') setShowUpper2(value);
    if (level === 'category') setShowCategory(value);
  };

  const handleToggleLevel = (level, nextValue) => {
    const { field, label } = LEVEL_META[level];
    if (nextValue) {
      // ON: 그냥 활성화
      setShowByLevel(level, true);
      return;
    }
    // OFF: 값 있으면 확인
    const anyValue = questions.some(
      (q) => trimWide(q[field] || '') !== ''
    );
    const doOff = () => {
      setShowByLevel(level, false);
      // 입력값도 초기화 (재토글 시 빈 상태로 시작)
      setQuestions((prev) => prev.map((q) => ({ ...q, [field]: '' })));
    };
    if (anyValue) {
      Modal.confirm({
        title: `${label}を非表示にしますか?`,
        content: '入力済みの値は全て削除されます (保存時に DB からも消えます)。',
        okText: '削除',
        okType: 'danger',
        cancelText: 'キャンセル',
        onOk: doOff,
      });
    } else {
      doOff();
    }
  };

  // -------------------------------------------------------------------
  //  保存
  // -------------------------------------------------------------------
  const handleSave = async () => {
    if (!hasAnyChanges) {
      message.info('変更がありません');
      return;
    }

    // 階層 ON인 경우 混合状態 체크 (사용자 사양):
    // - 검증 대상: 객관식(type:10)만. 서술형은 기존 데이터에서 카테고리 비어있는 경우가 정상.
    // - 객관식 일부만 비어있으면 → 다이얼로그
    // - 모든 질문(객관식+서술형) 전부 비어있으면 → 자동 OFF (조용히)
    // - 객관식 다 채워짐 → 서술형 값은 그대로 보존
    const checkLevels = [];
    if (showUpper1) checkLevels.push({ field: 'category_name_upper1', label: '大カテゴリ' });
    if (showUpper2) checkLevels.push({ field: 'category_name_upper2', label: '中カテゴリ' });
    if (showCategory) checkLevels.push({ field: 'category_name', label: '小カテゴリ' });

    const objective = questions.filter((q) => Number(q.type) === 10);
    const mixedIssues = [];
    const effectiveShow = {
      category_name_upper1: showUpper1,
      category_name_upper2: showUpper2,
      category_name: showCategory,
    };
    checkLevels.forEach(({ field, label }) => {
      // 객관식 기준 검증
      const objFilled = objective.filter(
        (q) => trimWide(q[field] || '') !== ''
      ).length;
      const objEmpty = objective.length - objFilled;
      // 자동 OFF 판정은 전 질문 기준 (서술형 값 손실 방지)
      const anyValue = questions.some(
        (q) => trimWide(q[field] || '') !== ''
      );

      if (!anyValue) {
        // 전 질문 비어있음 → 자동 OFF
        effectiveShow[field] = false;
      } else if (objFilled > 0 && objEmpty > 0) {
        // 객관식 일부만 비어있음 → 다이얼로그
        mixedIssues.push({ field, label, filled: objFilled, empty: objEmpty });
      }
      // 객관식 전부 채워짐 또는 객관식 다 비고 서술형만 있음 → 정상 저장 (effectiveShow 그대로)
    });

    if (mixedIssues.length > 0) {
      const confirmed = await new Promise((resolve) => {
        Modal.confirm({
          title: '客観式カテゴリの一部が未入力です',
          content: (
            <div>
              {mixedIssues.map((m, i) => (
                <div key={i}>
                  ・{m.label}: 客観式 {m.filled}件入力, {m.empty}件未入力
                </div>
              ))}
              <div style={{ marginTop: 8 }}>
                このまま保存すると、これらのカテゴリ階層は<b>全質問から削除</b>されます。続行しますか?
              </div>
              <div style={{ marginTop: 4, fontSize: 'var(--text-xs)', color: 'var(--grey-65)' }}>
                ※ 記述式の入力値もあわせて削除されます。
              </div>
            </div>
          ),
          okText: '削除して保存',
          okType: 'danger',
          cancelText: 'キャンセル',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
      mixedIssues.forEach((m) => {
        effectiveShow[m.field] = false;
      });
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('w_auth');
      const ops = [];

      // set_name 변경
      if (isSetNameChanged) {
        ops.push(
          Axios.patch(`/api/questionSet/${id}`, {
            set_name: setName,
            executorToken: token,
          })
        );
      }

      // 각 질문 별로 변경된 필드만 PATCH
      questions.forEach((q) => {
        const orig = originalQuestions[String(q._id)] || {};
        const payload = { executorToken: token };

        // question 텍스트
        if ((q.question || '') !== (orig.question || '')) {
          payload.question = q.question;
        }

        // category 系: effectiveShow 가 OFF면 빈 문자열 전송 → 서버에서 $unset
        ['category_name', 'category_name_upper1', 'category_name_upper2'].forEach(
          (field) => {
            const newVal = effectiveShow[field] ? trimWide(q[field] || '') : '';
            const origVal = trimWide(orig[field] || '');
            if (newVal !== origVal) {
              payload[field] = effectiveShow[field] ? q[field] || '' : '';
            }
          }
        );

        // executorToken 외에 실제 변경된 필드가 있을 때만 호출
        const hasRealChange = Object.keys(payload).some(
          (k) => k !== 'executorToken'
        );
        if (hasRealChange) {
          ops.push(Axios.patch(`/api/question/${q._id}`, payload));
        }
      });

      if (ops.length === 0) {
        message.info('変更がありません');
        setSaving(false);
        return;
      }

      const results = await Promise.all(ops);
      const failed = results.filter((r) => !r.data.success);
      if (failed.length > 0) {
        message.error(`${failed.length} 件の更新に失敗しました`);
      } else {
        message.success('保存しました');
        history.push('/questionsetmgr');
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      message.error(`保存エラー: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    history.push('/questionsetmgr');
  };

  // -------------------------------------------------------------------
  //  Render
  // -------------------------------------------------------------------
  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!questionSet) {
    return (
      <PageContainer>
        <Alert type="error" message="設問セットが見つかりません" showIcon />
        <Button
          style={{ marginTop: 'var(--space-4)' }}
          onClick={() => history.push('/questionsetmgr')}
        >
          一覧に戻る
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="設問セット編集"
        subtitle={questionSet.set_description || ''}
        extra={
          <Space>
            {hasAnyChanges ? (
              <Popconfirm
                title="変更を破棄しますか?"
                onConfirm={handleCancel}
                okText="破棄"
                cancelText="戻る"
              >
                <Button icon={<RollbackOutlined />}>キャンセル</Button>
              </Popconfirm>
            ) : (
              <Button icon={<RollbackOutlined />} onClick={handleCancel}>
                キャンセル
              </Button>
            )}
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!hasAnyChanges}
              onClick={handleSave}
            >
              保存
              {hasAnyChanges &&
                ` (${(isSetNameChanged ? 1 : 0) + changedQuestions.length})`}
            </Button>
          </Space>
        }
        divider
      />

      {/* --- セット名編集 --- */}
      <SectionCard title="セット情報" style={{ marginBottom: 'var(--space-6)' }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <div style={labelStyle}>セット名</div>
            <Input
              style={{ width: 360 }}
              value={setName}
              onChange={(e) => setSetName(e.target.value)}
            />
            {isSetNameChanged && (
              <Tag color="orange" style={{ marginLeft: 'var(--space-2)' }}>
                変更あり
              </Tag>
            )}
          </div>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--grey-65)' }}>
            形式: <Tag color="orange">{questionSet.type_360 || '-'}</Tag>
            区分: <Tag>{getSubTypeLabel(questionSet.sub_type)}</Tag>
          </div>
        </Space>
      </SectionCard>

      {/* --- カテゴリ階層 設定 (3レベル独立トグル) --- */}
      <SectionCard
        title="カテゴリ階層"
        style={{ marginBottom: 'var(--space-6)' }}
      >
        <Space wrap size="large">
          <Space size="small">
            <Switch
              checked={showUpper1}
              onChange={(v) => handleToggleLevel('upper1', v)}
            />
            <span>大カテゴリ</span>
          </Space>
          <Space size="small">
            <Switch
              checked={showUpper2}
              onChange={(v) => handleToggleLevel('upper2', v)}
            />
            <span>中カテゴリ</span>
          </Space>
          <Space size="small">
            <Switch
              checked={showCategory}
              onChange={(v) => handleToggleLevel('category', v)}
            />
            <span>小カテゴリ</span>
          </Space>
        </Space>
        <div
          style={{
            marginTop: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            color: 'var(--grey-65)',
          }}
        >
          ※ トグル操作は全質問に一括適用されます。OFF にすると入力済みの値は保存時に DB から削除されます。
        </div>
      </SectionCard>

      {/* --- 質問編集 --- */}
      <SectionCard
        title={`質問内容 (${questions.length} 問 / 変更 ${changedQuestions.length} 問)`}
      >
        {grouped.length === 0 ? (
          <Alert type="info" message="質問がありません" />
        ) : (
          <Collapse
            defaultActiveKey={grouped.map(([cat]) => cat)}
            items={grouped.map(([cat, items]) => ({
              key: cat,
              label: (
                <Space>
                  <b>{cat}</b>
                  <Tag color="orange">{items.length} 問</Tag>
                </Space>
              ),
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {items.map((q) => {
                    const changed = isQuestionChanged(q);
                    return (
                      <div
                        key={String(q._id)}
                        style={{
                          padding: 'var(--space-3)',
                          background: changed ? 'var(--orange-background)' : 'transparent',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <Space style={{ marginBottom: 'var(--space-2)' }}>
                          <Tag>{q.no}</Tag>
                          {q.type === '20' && <Tag color="blue">記述</Tag>}
                          {changed && <Tag color="orange">変更あり</Tag>}
                        </Space>
                        {/* 좌우 분할: 좌(2) 問題文 / 우(1) カテゴリ */}
                        <div
                          style={{
                            display: 'flex',
                            gap: 'var(--space-4)',
                            flexWrap: 'wrap',
                          }}
                        >
                          <div style={{ flex: '2 1 360px', minWidth: 280 }}>
                            <div style={labelStyle}>問題文</div>
                            <Input.TextArea
                              autoSize={{ minRows: 2, maxRows: 6 }}
                              value={q.question || ''}
                              onChange={(e) =>
                                updateField(q._id, 'question', e.target.value)
                              }
                            />
                          </div>
                          <div
                            style={{
                              flex: '1 1 220px',
                              minWidth: 200,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 'var(--space-2)',
                            }}
                          >
                            {showUpper1 && (
                              <div>
                                <div style={labelStyle}>大カテゴリ</div>
                                <Input
                                  value={q.category_name_upper1 || ''}
                                  onChange={(e) =>
                                    updateField(
                                      q._id,
                                      'category_name_upper1',
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            )}
                            {showUpper2 && (
                              <div>
                                <div style={labelStyle}>中カテゴリ</div>
                                <Input
                                  value={q.category_name_upper2 || ''}
                                  onChange={(e) =>
                                    updateField(
                                      q._id,
                                      'category_name_upper2',
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            )}
                            {showCategory && (
                              <div>
                                <div style={labelStyle}>小カテゴリ</div>
                                <Input
                                  value={q.category_name || ''}
                                  onChange={(e) =>
                                    updateField(
                                      q._id,
                                      'category_name',
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            )}
                            {!showUpper1 && !showUpper2 && !showCategory && (
                              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--grey-65)' }}>
                                (カテゴリ階層は全て OFF)
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Space>
              ),
            }))}
          />
        )}
      </SectionCard>
    </PageContainer>
  );
};

export default EditQSetPage;
